import { CrudStoreItemPartner360 } from 'legacy/lut/classes/models/partner360/CrudStoreItemPartner360'
import { IFormCommitment } from 'legacy/lut/classes/models/partner360/IFormCommitment'
import { IFormPartnerBasicInfo } from 'legacy/lut/classes/models/partner360/IFormPartnerBasicInfo'
import { useEffect, useState } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { Logic, Operator } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'

import { useSignature } from '../../../hooks/useSignature'

export interface ISigner extends IForm {
  item?: IFormCommitment
  name?: string
  email?: string
  contactId?: number
  date?: Date
  buttonCaption: string
  type: string
  comment?: string
  sendDate?: Date
  language?: string
}

export const partnerSignerDefault: ISigner = {
  buttonCaption: 'partnercommitment-sign-off',
  type: 'partner',
}

export const hpSignerDefault: ISigner = {
  buttonCaption: 'partnercommitment-sign-off',
  type: 'hp',
}

export const managerSignerDefault: ISigner = {
  buttonCaption: 'partnercommitment-sign-off',
  type: 'manager',
}

export interface ICommitmentInfo extends IForm {
  date: string
  email: string
  text: string
  comment: string
}

export const getDefaultCommitment = (commitmentType: string): ISigner => {
  return {
    buttonCaption: 'partnercommitment-sign-off',
    type: commitmentType,
  }
}

export const GetSignatureArray = (
  partnerInfo: IFormPartnerBasicInfo | IObjectAny,
  section: string
): { signed: number; dropSignature: (widget: string) => Promise<void> }[] => {
  return (partnerInfo['ProgramOfferings'] || '')
    .split(',')
    .filter((el: string) => el)
    .map((el: string) => {
      return GetSignatureId(partnerInfo.PartnerId, section, el)
    })
}

/**
 * @deprecated
 * Will be removed in the future. Use `lut/hooks/useSignature` instead.
 * */
export const GetSignatureId = (partnerId: string, section: string, currType: string) => {
  const { signatureId, dropSignature } = useSignature()
  return { signed: signatureId ? 1 : -1, dropSignature }
}

export interface IBPCompleteness {
  partnerId: string
  completenessThreshold: number
  actualCompleteness: number
}

export const GetBPCompleteness = (partnerId: string, fyear: number) => {
  const [bpCompleteness, setBPCompleteness] = useState<IBPCompleteness>({
    partnerId,
    completenessThreshold: 80,
    actualCompleteness: 0,
  })

  useEffect(() => {
    const basicInfoCrud = CrudStore.get(
      CrudStoreItemPartner360.PartnerBasicInfo,
      'completenessTreshold'
    )
    basicInfoCrud.setFilter({
      logic: Logic.AND,
      filters: [
        { field: 'PartnerId', value: partnerId, operator: Operator.EQUAL },
        { field: 'FYear', value: fyear, operator: Operator.EQUAL },
      ],
    })
    basicInfoCrud.fetch().then((result) => {
      CrudStore.get(CrudStoreItemPartner360.JBPSettings)
        .getById({ id: 1, keyField: 'EntryId' })
        .then((options) => {
          setBPCompleteness({
            partnerId,
            actualCompleteness: result ? (result.items[0] || {})['CompletenessTotal'] * 100 : 0,
            completenessThreshold: options ? options.CommitmentCompletenessThreshold : 80,
          })
        })
    })
  }, [partnerId])

  return bpCompleteness
}

export const checkBPCompleteness = (item: IBPCompleteness) => {
  return item.actualCompleteness >= item.completenessThreshold
}

// const timer = async (ms: number) => {
//   return new Promise((res) => setTimeout(res, ms))
// }

export const waitingForFile = async (
  pid: string,
  fyear: number,
  type: string,
  setProgress?: (value: number) => void
) => {
  const crudFiles = CrudStore.get(CrudStoreItemPartner360.JBPBPExportFiles)
  for (let i = 0; i < 10; i++) {
    await new Promise((res) => setTimeout(res, 3000)) //timer(3000)
    const item = await crudFiles.getById({
      id: `${pid}_${fyear}`,
      force: true,
      keyField: 'UniqueId',
    })
    const exportedFileFieldSuffix = type ? type.replaceAll(' ', '') : ''
    if (item && item[`ExportedFile${exportedFileFieldSuffix}`]) {
      return true
    }
    setProgress && setProgress(10 + 5 * i)
  }
  return false
}

export const getHPSigner = async (partnerId: string, programType: string) => {
  const crudHPSign = CrudStore.get(CrudStoreItemPartner360.Commitment, 'CheckHPSign')
  crudHPSign.setFilter({
    logic: Logic.AND,
    filters: [
      {
        field: 'PartnerID',
        operator: Operator.EQUAL,
        value: partnerId,
      },
      {
        field: 'CommitmentType',
        operator: Operator.EQUAL,
        value: 'hp',
      },
      {
        field: 'ProgramType',
        operator: Operator.EQUAL,
        value: programType,
      },
    ],
  })
  crudHPSign.setPaging({ page: 0, pageSize: 1 })
  const result = await crudHPSign.fetch()
  const commitmentInfo = result.items || []
  if (commitmentInfo.length === 0) {
    return hpSignerDefault
  } else {
    if (!commitmentInfo[0].EntryId) {
      return hpSignerDefault
    }
    return (commitmentInfo[0].Dropped || 0) === 0
      ? {
          name: commitmentInfo[0].SignerName,
          date: commitmentInfo[0].SignoffDate,
          buttonCaption: 'partnercommitment-resign',
          type: 'hp',
          item: commitmentInfo[0],
        }
      : hpSignerDefault
  }
}

export const getPartnerBySigner = async (signerEmail: string) => {
  let partnerId = ''
  let programType = ''

  const crudOptions = CrudStore.get(CrudStoreItemPartner360.JBPSettings)
  const options = await crudOptions.getById({ id: 1, keyField: 'EntryId' })
  if (options && options.CurrentFYear) {
    const commitmentCrud = CrudStore.get(CrudStoreItemPartner360.Commitment, 'CheckHPSign')
    commitmentCrud.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'CommitmentType',
          operator: Operator.EQUAL,
          value: 'partner',
        },
        {
          field: 'FYear',
          operator: Operator.EQUAL,
          value: options.CurrentFYear,
        },
        {
          field: 'SignerEmail',
          operator: Operator.EQUAL,
          value: signerEmail,
        },
      ],
    })
    commitmentCrud.setSort([
      { field: 'SignoffDate', dir: 'asc' },
      { field: 'Modified', dir: 'desc' },
    ])
    commitmentCrud.setPaging({ page: 0, pageSize: 1 })
    const commitmentResult = await commitmentCrud.fetch()

    if (commitmentResult.items.length) {
      partnerId = commitmentResult.items[0].PartnerID
      programType = commitmentResult.items[0].ProgramType
    }
  }

  return { partnerId, programType }
}
